/**
 * SessionRankComparison — reads this session's ranking next to another
 * session's, for the items both ranked. Sits on the Results tab.
 *
 * Any session in the workspace that ranked at least one of the same items can
 * be picked, whatever tool it used or wherever it was run.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { compareSessionRankings, sharedItemCount, type RankComparisonRow } from '../../../lib/sessionRankComparison';
import { DataTable, type Column } from '@/components/ds';
import { useItemHref } from '@/lib/itemLinkScope';

function differenceLabel(difference: number): string {
  if (difference === 0) return 'Same place';
  const places = Math.abs(difference);
  const word = places === 1 ? 'place' : 'places';
  return difference > 0 ? `${places} ${word} higher there` : `${places} ${word} higher here`;
}

export function SessionRankComparison({ session }: { session: PrioritizationSession }) {
  const itemHref = useItemHref();
  const sessions = useSessionStore((state) => state.sessions);
  const items = useItemsStore((state) => state.items);
  const [otherId, setOtherId] = useState('');

  const hereIds = useMemo(() => session.rankingOutput?.rankedItemIds ?? [], [session.rankingOutput]);

  const candidates = useMemo(
    () =>
      sessions
        .filter((s) => s.id !== session.id)
        .map((s) => ({ session: s, shared: sharedItemCount(hereIds, s.rankingOutput?.rankedItemIds ?? []) }))
        .filter((c) => c.shared > 0)
        .sort((a, b) => b.shared - a.shared),
    [sessions, session.id, hereIds],
  );

  const other = candidates.find((c) => c.session.id === otherId)?.session;
  const thereIds = useMemo(() => other?.rankingOutput?.rankedItemIds ?? [], [other]);
  const rows = useMemo(() => (other ? compareSessionRankings(hereIds, thereIds) : []), [other, hereIds, thereIds]);

  const columns: Column<RankComparisonRow>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (row) => (
        <Link to={itemHref(row.itemId)} className="font-medium text-[var(--text-pri)] hover:underline">
          {items.find((i) => i.id === row.itemId)?.title ?? row.itemId}
        </Link>
      ),
    },
    {
      key: 'here',
      header: 'This session',
      width: 140,
      render: (row) => (
        <>
          <span className="font-bold">#{row.rankHere}</span>
          <span className="text-[var(--text-sec)]"> of {hereIds.length}</span>
        </>
      ),
    },
    {
      key: 'there',
      header: 'That session',
      width: 140,
      render: (row) => (
        <>
          <span className="font-bold">#{row.rankThere}</span>
          <span className="text-[var(--text-sec)]"> of {thereIds.length}</span>
        </>
      ),
    },
    {
      key: 'difference',
      header: 'Among the items in both',
      width: 200,
      render: (row) => differenceLabel(row.sharedDifference),
    },
  ];

  if (candidates.length === 0) return null;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)]" data-testid="session-rank-comparison">
      <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-[var(--rule)]">
        <label htmlFor="compare-session" className="text-sm font-medium text-[var(--text-pri)]">
          Compare with another ranking
        </label>
        <select
          id="compare-session"
          value={otherId}
          onChange={(e) => setOtherId(e.target.value)}
          className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)] text-[var(--text-pri)] max-w-full"
        >
          <option value="">Choose a session…</option>
          {candidates.map((c) => (
            <option key={c.session.id} value={c.session.id}>
              {c.session.name} ({c.shared} of the same items)
            </option>
          ))}
        </select>
      </div>

      {other && (
        <>
          <p className="px-4 pt-3 text-sm text-[var(--text-sec)]">
            {rows.length} items were ranked in both. This session ranked {hereIds.length}; “{other.name}” ranked{' '}
            {thereIds.length}.{' '}
            <Link to={`/prioritization/sessions/${other.id}?tab=results`} className="underline hover:text-[var(--text-pri)]">
              Open that session
            </Link>
          </p>
          <div className="mt-2">
            <DataTable<RankComparisonRow>
              ariaLabel="Items ranked in both sessions"
              columns={columns}
              rows={rows}
              getRowKey={(row) => row.itemId}
            />
          </div>
        </>
      )}
    </div>
  );
}
